import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { connect } from 'react-redux';
import { CalendarDays, Plus, PieChart, History, Calendar, AlertCircle, AlertTriangle } from 'lucide-react';
import PageShell from '../../../components/leaves/PageShell';
import LeaveBalancesCards from './components/LeaveBalancesCards';
import LeaveHistoryList from './components/LeaveHistoryList';
import LeaveApplicationForm from './components/LeaveApplicationForm';
import LeaveDetailsModal from './components/LeaveDetailsModal';
import HolidayCalendar from './components/HolidayCalendar';
import UndoToast from '../../../components/leaves/UndoToast';
import { LeaveRequestView, LeaveBalanceView, LeaveRequestInput, LeaveTypeView, LeaveRequestStatus, UpcomingHolidayView } from '../../../models/attendance';
import { HolidayView } from '../../../models/general';
import * as LeavesApi from '../../../api/Attendance/leaves';
import { getLeaveTypes, requestLeave, addLeaveAttachments } from '../../../api/Attendance/leaves';
import { getUserHolidays } from '../../../api/General/holidays';
import { useWriteAccess } from '../../../hooks/useWriteAccess';
import RestrictedWriteNotice from '../../../components/subscription/RestrictedWriteNotice';
import { getApiErrorMessage } from '../../../utils/apiErrors';

interface TotalStats {
  totalRemainingDays: number;
  totalUsedDays: number;
  pendingRequests: number;
  approvedThisMonth: number;
}

interface MyLeavesProps {
  userId: string;
  company: any;
  role: string;
}

const MyLeaves: React.FC<MyLeavesProps> = ({ userId }) => {
  const { t } = useTranslation(['leaves', 'common']);
  // CO-24 — /api/Leave is NOT on the server's RestrictedWriteAllowlist, so this write genuinely 403s.
  const write = useWriteAccess();

  const [loading, setLoading] = useState(true);
  const [balances, setBalances] = useState<LeaveBalanceView[]>([]);
  const [leaveHistory, setLeaveHistory] = useState<LeaveRequestView[]>([]);
  const [leaveTypes, setLeaveTypes] = useState<LeaveTypeView[]>([]);
  const [holidays, setHolidays] = useState<HolidayView[]>([]);
  const [upcomingHolidays, setUpcomingHolidays] = useState<UpcomingHolidayView[]>([]);

  const [showApplicationForm, setShowApplicationForm] = useState(false);
  const [selectedLeaveRecord, setSelectedLeaveRecord] = useState<LeaveRequestView | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [toast, setToast] = useState<{ message: string; type: 'success' | 'error' } | null>(null);

  // Undo toast state — timer logic lives inside UndoToast component
  const [undoRequestId, setUndoRequestId] = useState<number | null>(null);
  const [cancelConfirmRecord, setCancelConfirmRecord] = useState<LeaveRequestView | null>(null);
  const [cancelling, setCancelling] = useState(false);

  const showToast = (message: string, type: 'success' | 'error') => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 4000);
  };

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [balancesRes, leavesRes, typesRes, holidaysRes, upcomingRes] = await Promise.all([
        LeavesApi.getMyBalances(),
        LeavesApi.getMyLeaves({ pageSize: 50 }),
        getLeaveTypes(userId),
        getUserHolidays(userId),
        LeavesApi.getUpcomingHolidays(90),
      ]);

      if (balancesRes.errors.length === 0) {
        setBalances(balancesRes.payload);
      }
      if (leavesRes.errors.length === 0) {
        setLeaveHistory(leavesRes.payload.items);
      }
      if (typesRes.errors.length === 0) {
        setLeaveTypes(typesRes.payload);
      }
      if (holidaysRes.errors.length === 0) {
        setHolidays(holidaysRes.payload);
      }
      if (upcomingRes.errors.length === 0) {
        setUpcomingHolidays(upcomingRes.payload);
      }
    } catch {
      setError(t('Error loading data'));
    }
    setLoading(false);
  }, [userId, t]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleUndoSubmission = async () => {
    if (undoRequestId === null) return;
    const requestId = undoRequestId;
    setUndoRequestId(null);
    try {
      const result = await LeavesApi.cancelLeaveRequest(requestId);
      if (result.errors.length === 0) {
        showToast(t('undoSuccess'), 'success');
        fetchData();
      } else {
        showToast(getApiErrorMessage(result, t, t('Submission failed')), 'error');
      }
    } catch (err) {
      showToast(getApiErrorMessage(err, t, t('Submission failed')), 'error');
    }
  };

  const handleLeaveSubmit = async (input: LeaveRequestInput, files: File[]) => {
    setSubmitting(true);
    try {
      const result = await requestLeave(input);
      if (result.errors.length === 0) {
        // Upload attachments if any
        if (files.length > 0) {
          try {
            await addLeaveAttachments({
              leaveRequestId: result.payload.id,
              attachments: files,
            });
          } catch (uploadErr) {
            // Rollback: cancel the leave request since attachments failed
            try {
              await LeavesApi.cancelLeaveRequest(result.payload.id);
            } catch { /* best-effort rollback */ }
            showToast(getApiErrorMessage(uploadErr, t, t('Attachment upload failed')), 'error');
            setSubmitting(false);
            return;
          }
        }
        setShowApplicationForm(false);
        setUndoRequestId(result.payload.id);
        fetchData();
      } else {
        showToast(getApiErrorMessage(result, t, t('Submission failed')), 'error');
      }
    } catch (err) {
      showToast(getApiErrorMessage(err, t, t('Submission failed')), 'error');
    }
    setSubmitting(false);
  };

  const handleCancelRequest = (record: LeaveRequestView) => {
    setCancelConfirmRecord(record);
  };

  const handleConfirmCancel = async () => {
    if (!cancelConfirmRecord) return;
    setCancelling(true);
    try {
      const result = await LeavesApi.cancelLeaveRequest(cancelConfirmRecord.id);
      if (result.errors.length === 0) {
        showToast(t('cancelSuccess'), 'success');
        setSelectedLeaveRecord(null);
        setCancelConfirmRecord(null);
        fetchData();
      } else {
        showToast(getApiErrorMessage(result, t, t('Submission failed')), 'error');
      }
    } catch (err) {
      showToast(getApiErrorMessage(err, t, t('Submission failed')), 'error');
    }
    setCancelling(false);
  };

  const calculateTotalStats = (): TotalStats => {
    const totalUsedDays = Math.round(balances.reduce((sum, b) => sum + (b.used ?? 0), 0) * 100) / 100;
    const totalRemainingDays = Math.round(balances.reduce((sum, b) => sum + b.balance, 0) * 100) / 100;
    const pendingRequests = leaveHistory.filter(l => l.status === LeaveRequestStatus.Pending).length;
    const now = new Date();
    const approvedThisMonth = leaveHistory.filter(l =>
      l.status === LeaveRequestStatus.Approved &&
      new Date(l.lastUpdate).getMonth() === now.getMonth() &&
      new Date(l.lastUpdate).getFullYear() === now.getFullYear()
    ).length;

    return { totalUsedDays, totalRemainingDays, pendingRequests, approvedThisMonth };
  };

  const stats = calculateTotalStats();

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-600 mx-auto mb-4" />
          <p className="text-muted-foreground">{t('Loading')}...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <AlertCircle size={48} className="mx-auto mb-4 text-red-500" />
          <p className="text-foreground mb-4">{error}</p>
          <button
            onClick={fetchData}
            data-testid="leave-retry-btn"
            className="px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700"
          >
            {t('Retry')}
          </button>
        </div>
      </div>
    );
  }

  return (
    <PageShell
      title={t('My Leaves')}
      description={t('myLeavesDescription')}
      icon={CalendarDays}
      accentColor="emerald"
      stats={[
        { label: t('Days Remaining'), value: stats.totalRemainingDays, color: 'text-emerald-600' },
        { label: t('Days Used'), value: stats.totalUsedDays },
        { label: t('Pending Requests'), value: stats.pendingRequests, color: 'text-orange-600' },
        { label: t('Approved This Month'), value: stats.approvedThisMonth, color: 'text-blue-600' },
      ]}
      className="min-h-screen bg-background"
    >
      {/* Apply for Leave Button */}
      <RestrictedWriteNotice className="mt-6" />
      <div className="py-6">
        <button
          onClick={() => setShowApplicationForm(true)}
          data-testid="apply-leave-btn"
          {...write.buttonProps}
          className="inline-flex items-center px-6 py-3 bg-emerald-600 hover:bg-emerald-700 text-white font-medium rounded-lg transition-colors shadow-lg hover:shadow-xl disabled:cursor-not-allowed disabled:opacity-50 disabled:shadow-none disabled:hover:bg-emerald-600"
        >
          <Plus size={20} className="ltr:mr-2 rtl:ml-2" />
          {t('Apply for Leave')}
        </button>
      </div>

      {/* Main Content */}
      <div className="pb-8">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Left Column - Leave Balances and Leave History */}
          <div className="lg:col-span-2 space-y-6">
            {/* Leave Balances Section */}
            <div className="bg-card border border-border rounded-xl" data-testid="leave-balances-section">
              <div className="p-6 border-b border-border">
                <h2 className="text-xl font-semibold text-foreground flex items-center">
                  <PieChart size={20} className="ltr:mr-2 rtl:ml-2" />
                  {t('Leave Balances')}
                </h2>
                <p className="text-sm text-muted-foreground mt-1">{t('balancesDescription')}</p>
              </div>
              <div className="p-6">
                <LeaveBalancesCards balances={balances} />
              </div>
            </div>

            {/* Leave History Section */}
            <div className="bg-card border border-border rounded-xl" data-testid="leave-history-section">
              <div className="p-6 border-b border-border">
                <h2 className="text-xl font-semibold text-foreground flex items-center">
                  <History size={20} className="ltr:mr-2 rtl:ml-2" />
                  {t('Leave History')}
                </h2>
                <p className="text-sm text-muted-foreground mt-1">{t('historyDescription')}</p>
              </div>
              <div className="p-6">
                <LeaveHistoryList
                  history={leaveHistory}
                  onViewDetails={(record) => setSelectedLeaveRecord(record)}
                  onCancel={handleCancelRequest}
                />
              </div>
            </div>
          </div>

          {/* Right Column - Holiday Calendar */}
          <div className="space-y-6">
            <div className="bg-card border border-border rounded-xl" data-testid="holiday-calendar">
              <div className="p-6 border-b border-border">
                <h2 className="text-xl font-semibold text-foreground flex items-center">
                  <Calendar size={20} className="ltr:mr-2 rtl:ml-2" />
                  {t('Holiday Calendar')}
                </h2>
                <p className="text-sm text-muted-foreground mt-1">{t('holidayCalendarDescription')}</p>
              </div>
              <div className="p-6">
                <HolidayCalendar
                  holidays={holidays}
                  upcomingHolidays={upcomingHolidays}
                />
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Leave Application Form Modal */}
      {showApplicationForm && (
        <LeaveApplicationForm
          onSubmit={handleLeaveSubmit}
          onCancel={() => setShowApplicationForm(false)}
          balances={balances}
          leaveTypes={leaveTypes}
          userId={userId}
          submitting={submitting}
        />
      )}

      {/* Leave Details Modal */}
      {selectedLeaveRecord && (
        <LeaveDetailsModal
          record={selectedLeaveRecord}
          onClose={() => setSelectedLeaveRecord(null)}
          onCancel={handleCancelRequest}
        />
      )}

      {/* Cancel Confirmation Dialog */}
      {cancelConfirmRecord && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-card border border-border rounded-xl max-w-md w-full" data-testid="cancel-confirm-dialog">
            <div className="p-6 text-center">
              <div className="mx-auto w-12 h-12 rounded-full bg-red-100 flex items-center justify-center mb-4">
                <AlertTriangle size={24} className="text-red-600" />
              </div>
              <h3 className="text-lg font-semibold text-foreground mb-2">
                {cancelConfirmRecord.status === LeaveRequestStatus.Approved
                  ? t('confirmCancelLeaveApprovedTitle')
                  : t('confirmCancelLeaveTitle')}
              </h3>
              <p className="text-sm text-muted-foreground mb-1">
                <span className="font-medium text-foreground">{cancelConfirmRecord.leaveType}</span>
                {' — '}
                {new Date(cancelConfirmRecord.startDate).toLocaleDateString()} - {new Date(cancelConfirmRecord.endDate).toLocaleDateString()}
                {' '}({Math.round(cancelConfirmRecord.numberOfDays * 100) / 100} {cancelConfirmRecord.numberOfDays === 1 ? t('day') : t('days')})
              </p>
              <p className="text-sm text-muted-foreground">
                {cancelConfirmRecord.status === LeaveRequestStatus.Approved
                  ? t('confirmCancelLeaveApprovedMessage')
                  : t('confirmCancelLeaveMessage')}
              </p>
            </div>
            <div className="flex items-center justify-end gap-3 px-6 pb-6">
              <button
                onClick={() => setCancelConfirmRecord(null)}
                disabled={cancelling}
                data-testid="cancel-confirm-keep-btn"
                className="px-4 py-2 text-sm font-medium text-foreground border border-border rounded-lg hover:bg-muted transition-colors"
              >
                {t('keepRequest')}
              </button>
              <button
                onClick={handleConfirmCancel}
                disabled={cancelling}
                data-testid="cancel-confirm-yes-btn"
                className="px-4 py-2 text-sm font-medium text-white bg-red-600 hover:bg-red-700 rounded-lg transition-colors disabled:opacity-50"
              >
                {cancelling ? t('Cancelling') : (cancelConfirmRecord.status === LeaveRequestStatus.Approved ? t('Request Cancellation') : t('Cancel Request'))}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Toast notification */}
      {toast && (
        <div className={`fixed bottom-8 left-1/2 transform -translate-x-1/2 px-6 py-3 rounded-lg shadow-lg text-sm font-medium z-50 transition-all ${
          toast.type === 'success' ? 'bg-green-600 text-white' : 'bg-red-600 text-white'
        }`}>
          {toast.message}
        </div>
      )}

      {/* Undo toast with countdown */}
      <UndoToast
        open={undoRequestId !== null}
        message={t('leaveRequestSubmitted')}
        onUndo={handleUndoSubmission}
        onDismiss={() => setUndoRequestId(null)}
        variant="success"
      />
    </PageShell>
  );
};

const mapStateToProps = (state: any) => ({
  userId: state.user.userId,
  company: state.user.company,
  role: state.user.role,
});

export default connect(mapStateToProps)(MyLeaves);
